import cn from "classnames";

import { CarouselCentered } from "@/components/carouselCentered/carouselCentered.component";
import { StarRating } from "@/components/starRating/starRating.component";
import { Text } from "@/components/text/text.component";

import type { CustomerReviewsSection as CustomerReviewsSectionType } from "@/sanity.types";

import { CustomerReviewQuote } from "./customerReviewQuote.component";

type Review = NonNullable<CustomerReviewsSectionType["reviews"]>[number];

const CustomerReviewCard = ({
  review,
  className,
}: {
  review: Review;
  className?: string;
}) => (
  <article
    className={cn(
      "flex h-full min-w-0 flex-col justify-between rounded-2xl bg-white p-6 md:p-8",
      className,
    )}
    data-testid="customer-review-card">
    <div>
      <div
        className="mb-4 flex gap-1"
        aria-label={`${review.rating ?? 5} out of 5 stars`}>
        <StarRating
          rating={review.rating ?? 5}
          width={20}
          height={20}
          className="fill-secondary"
        />
      </div>
      {review.quote ? <CustomerReviewQuote quote={review.quote} /> : null}
    </div>

    <div className="mt-6">
      {review.authorName ? (
        <Text variant="body4" component="p" className="text-greyscale-body">
          {review.authorName}
        </Text>
      ) : null}
      {review.authorLocation ? (
        <Text
          variant="body6"
          component="p"
          className="text-greyscale-body mt-1">
          {review.authorLocation}
        </Text>
      ) : null}
    </div>
  </article>
);

const SectionHeading = ({ heading }: { heading?: string }) => (
  <div className="w-full shrink-0 px-4 lg:max-w-[320px] lg:px-0">
    <Text
      variant="h1"
      component="span"
      aria-hidden="true"
      className="text-secondary block h-12 pl-[40px] text-left leading-none !text-[80px] lg:pl-0">
      &ldquo;
    </Text>
    {heading ? (
      <Text
        variant="h3"
        component="h2"
        className="text-greyscale-headline mt-2 px-4 text-center lg:mt-2 lg:px-0 lg:text-left">
        {heading}
      </Text>
    ) : null}
  </div>
);

export const CustomerReviewsSection = ({
  block,
}: CustomerReviewsSectionProps) => {
  const reviews = block.reviews?.filter((review) => review.quote?.trim());

  if (!reviews?.length) return null;

  return (
    <section
      aria-label={block.heading ?? "Customer reviews"}
      className="bg-primary-100 w-full py-10 md:py-14 lg:py-16"
      data-testid="customer-reviews-section">
      <div className="container mx-auto flex flex-col items-center gap-8 md:gap-10 lg:flex-row lg:items-start lg:gap-12 lg:px-9">
        {/* Fixed heading column — does not scroll with the carousel */}
        <div className="w-full shrink-0 lg:w-1/3 lg:max-w-[320px]">
          <SectionHeading heading={block.heading} />
        </div>

        <CarouselCentered
          className="w-full min-w-0 lg:flex-1"
          data-testid="customer-reviews-carousel"
          swiperItemsClassName="self-stretch"
          slideInnerClassName="h-full"
          mobileSlidesPerView={1.3}
          desktopSlidesPerView={3}
          mobileSpaceBetween={20}
          desktopSpaceBetween={24}
          mobileSlidesPerGroup={1}
          desktopSlidesPerGroup={3}
          centeredSlides
          desktopCenteredSlides={false}
          initialSlide={0}
          equalSlideHeights>
          {reviews.map((review) => (
            <CustomerReviewCard
              key={review._key}
              review={review}
              className="min-h-[280px]"
            />
          ))}
        </CarouselCentered>
      </div>
    </section>
  );
};

export type CustomerReviewsSectionProps = {
  block: CustomerReviewsSectionType;
};
